import React from 'react';
import HelpArticle from '../../components/help/HelpArticle';

const PaymentManagement: React.FC = () => {
  const content = (
    <>
      <h2>Håndtér betalinger</h2>
      <p>
        DoMore forbinder værktøjsejere med lejere, men håndterer ikke betalinger gennem platformen. 
        Her er en guide til hvordan du bedst håndterer betaling mellem parterne.
      </p>

      <h3>Vigtigt at vide</h3>
      <p>
        <strong>DoMore er ikke involveret i betalinger.</strong> Det betyder:
      </p>
      <ul>
        <li>Betaling sker direkte mellem lejer og ejer</li>
        <li>I vælger selv betalingsmetode</li>
        <li>I aftaler selv tidspunkt for betaling</li>
        <li>DoMore håndterer ingen penge</li>
      </ul>

      <h3>Aftale betalingsmetode</h3>
      <p>
        Når en booking er bekræftet, brug chatfunktionen til at aftale:
      </p>
      <ul>
        <li>Hvilken betalingsmetode I vil bruge</li>
        <li>Hvornår betalingen skal ske</li>
        <li>Om der skal betales depositum</li>
        <li>Hvordan depositum returneres</li>
      </ul>

      <h3>Anbefalede betalingsmetoder</h3>
      <p>
        Her er sikre og nemme måder at betale på:
      </p>

      <h4>MobilePay</h4>
      <p>
        Den mest populære metode i Danmark:
      </p>
      <ul>
        <li>✅ Hurtig og nem overførsel</li>
        <li>✅ Begge har sandsynligvis allerede appen</li>
        <li>✅ Gratis for private transaktioner</li>
        <li>✅ Dokumentation i app-historik</li>
      </ul>
      <p>
        <strong>Sådan:</strong> Del dit MobilePay nummer og bed lejeren sende beløbet.
      </p>

      <h4>Bankoverførsel</h4>
      <p>
        Traditionel og sikker:
      </p>
      <ul>
        <li>✅ Officiel dokumentation</li>
        <li>✅ God til større beløb</li>
        <li>✅ Alle har en bankkonto</li>
        <li>⚠️ Kan tage 1-2 dage</li>
      </ul>
      <p>
        <strong>Sådan:</strong> Del dit kontonummer (reg. og konto) og bed om overførsel med booking reference.
      </p>

      <h4>Kontanter</h4>
      <p>
        Simpelt men kræver ekstra opmærksomhed:
      </p>
      <ul>
        <li>✅ Øjeblikkelig betaling</li>
        <li>✅ Ingen app nødvendig</li>
        <li>⚠️ Ingen automatisk dokumentation</li>
        <li>⚠️ Husk at få kvittering</li>
      </ul>
      <p>
        <strong>Tip:</strong> Skriv altid en simpel kvittering når I handler kontant.
      </p>

      <h4>Swish (for svenske brugere)</h4>
      <p>
        Sveriges svar på MobilePay:
      </p>
      <ul>
        <li>✅ Fungerer som MobilePay</li>
        <li>✅ Bredt udbredt i Sverige</li>
      </ul>

      <h3>Hvornår skal der betales?</h3>
      <p>
        Der er forskellige tilgange:
      </p>

      <h4>Ved afhentning (mest almindeligt)</h4>
      <ul>
        <li>Lejeren betaler når værktøjet hentes</li>
        <li>Ejeren kan inspicere værktøjet først</li>
        <li>Begge parter er til stede</li>
      </ul>

      <h4>Ved på forhånd (for pålidelige lejere)</h4>
      <ul>
        <li>Betaling før afhentning</li>
        <li>Giver ejeren sikkerhed</li>
        <li>Kræver tillid til at værktøjet som aftalt</li>
      </ul>

      <h4>Ved returnering</h4>
      <ul>
        <li>Sjældent brugt</li>
        <li>Kun hvis ejeren er meget tryg ved lejeren</li>
        <li>Risiko for at lejeren ikke betaler</li>
      </ul>

      <h4>Opdelt betaling</h4>
      <ul>
        <li>Depositum ved afhentning</li>
        <li>Rest ved returnering</li>
        <li>God til dyrt værktøj</li>
      </ul>

      <h3>Depositum</h3>
      <p>
        Ejere kan kræve depositum for:
      </p>
      <ul>
        <li>Dyrt værktøj</li>
        <li>Værktøj der nemt kan beskadiges</li>
        <li>Lejere uden ratings</li>
      </ul>

      <h4>Sådan håndteres depositum</h4>
      <ul>
        <li>Aftal depositum-størrelse på forhånd</li>
        <li>Depositum betales ved afhentning</li>
        <li>Returneres når værktøj er afleveret i god stand</li>
        <li>Brug samme betalingsmetode frem og tilbage</li>
      </ul>

      <h4>Depositum med MobilePay</h4>
      <ol>
        <li>Lejer sender leje + depositum ved afhentning</li>
        <li>Ejer sender depositum tilbage ved returnering</li>
      </ol>

      <h3>Dokumentation er vigtig</h3>
      <p>
        Sørg altid for at have bevis:
      </p>
      <ul>
        <li>Gem kvitteringer eller transaktionshistorik</li>
        <li>Tag screenshot af MobilePay/bankoverførsler</li>
        <li>Skriv kvittering ved kontant betaling</li>
        <li>Noter booking ID fra DoMore</li>
      </ul>

      <h3>Kvittering template</h3>
      <p>
        Hvis du vil skrive en kvittering:
      </p>
      <blockquote>
        <strong>Kvittering for værktøjsleje</strong><br/>
        Dato: [dato]<br/>
        Værktøj: [navn]<br/>
        Lejeperiode: [fra] - [til]<br/>
        Pris: [beløb] DKK<br/>
        Depositum: [beløb] DKK<br/>
        Betalt via: [metode]<br/>
        <br/>
        Underskrift ejer: _____________<br/>
        Underskrift lejer: _____________
      </blockquote>

      <h3>Hvis der er betalingsproblemer</h3>
      <p>
        Ved uenigheder om betaling:
      </p>
      <ul>
        <li>Kommunikér åbent og respektfuldt</li>
        <li>Revisér jeres DoMore chat-historik</li>
        <li>Del dokumentation (screenshots, kvitteringer)</li>
        <li>Kontakt DoMore support hvis I ikke kan løse det</li>
      </ul>

      <h3>Beskyttelse mod svindel</h3>
      <p>
        For lejere:
      </p>
      <ul>
        <li>Betal aldrig før du ser værktøjet</li>
        <li>Tjek ejerens ratings</li>
        <li>Mød på en offentlig plads hvis du er usikker</li>
        <li>Få altid dokumentation for din betaling</li>
      </ul>

      <p>
        For ejere:
      </p>
      <ul>
        <li>Tjek lejers ratings</li>
        <li>Kræv depositum for dyrt værktøj</li>
        <li>Verificér betaling før værktøjet forlader din hånd</li>
        <li>Dokumentér alt</li>
      </ul>

      <h3>Priser og moms</h3>
      <p>
        For private udlejere:
      </p>
      <ul>
        <li>Lejere betaler den pris der står i appen</li>
        <li>Ingen moms på private transaktioner</li>
        <li>Den fulde pris går til ejeren</li>
      </ul>

      <p>
        For erhvervslejere:
      </p>
      <ul>
        <li>Du skal selv håndtere moms</li>
        <li>Udsted faktura når det kræves</li>
        <li>Følg skatteregler for udlejning</li>
      </ul>

      <h3>Ofte stillede spørgsmål</h3>

      <h4>Hvad hvis lejeren ikke betaler?</h4>
      <p>
        Udlevér ikke værktøjet før betaling er modtaget. Hvis problemer opstår efter udlevering, kontakt DoMore support.
      </p>

      <h4>Kan jeg få mit depositum tilbage med det samme?</h4>
      <p>
        Det afhænger af ejerens politik. Typisk returneres det ved aflevering hvis værktøjet er OK.
      </p>

      <h4>Skal jeg betale ekstra gebyrer?</h4>
      <p>
        Nej, den pris du ser i appen er den fulde pris. DoMore tager ikke provision fra transaktioner mellem brugere.
      </p>

      <h4>Hvad hvis værktøjet koster mere end aftalt?</h4>
      <p>
        Prisen i booking skal overholdes. Ved uenigheder, kontakt DoMore support med dokumentation.
      </p>
    </>
  );

  return (
    <HelpArticle
      title="Håndtér betalinger"
      category="Betaling"
      content={content}
      relatedArticles={[
        { title: 'Sikker betaling', link: '/help/secure-payment' },
        { title: 'Forstå priser', link: '/help/understand-pricing' },
      ]}
    />
  );
};

export default PaymentManagement;
